import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
} from "recharts";

const COLORS = [
    "#6F3AFF",
    "#3AA6FF",
    "#18C7A7",
    "#FFB347",
    "#EF4444",
    "#8A87A8",
    "#39C0ED",
    "#A78BFA",
    "#52EA91",
    "#F59E0B",
];

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function formatInt(value) {
    return Number(value ?? 0).toLocaleString("de-DE");
}

function formatPct(value) {
    return `${Number(value ?? 0).toFixed(1)}%`;
}

function formatTypeName(value) {
    return String(value || "UNKNOWN_TYPE").trim();
}

function normalizeRows(raw) {
    const payload = raw?.data ?? raw ?? {};
    const rows = Array.isArray(payload)
        ? payload
        : payload?.data || payload?.rows || payload?.items || [];

    if (!Array.isArray(rows)) return [];

    return rows
        .map((row) => {
            const name = formatTypeName(
                row.remediation_type ||
                row.REMEDIATION_TYPE ||
                row.TYPE_NAME ||
                row.type_name ||
                row.name
            );

            const open = toNumber(
                row.OPEN ??
                row.open ??
                row.open_cases ??
                row.current?.OPEN ??
                row.current?.open,
                0
            );

            const closed = toNumber(
                row.CLOSED ??
                row.closed ??
                row.closed_cases ??
                row.current?.CLOSED ??
                row.current?.closed,
                0
            );

            const total = toNumber(
                row.total ??
                row.TOTAL ??
                row.total_cases ??
                row.AMOUNT ??
                row.amount,
                open + closed
            );

            return {
                name,
                value: total,
                open,
                closed,
            };
        })
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
}

const TOOLTIP_ESTIMATE = { width: 220, height: 138 };
const TOOLTIP_MARGIN = 10;
const TOOLTIP_RADIAL_OFFSET = 16;

function getOverviewTooltipPosition(coordinate, chartWidth, chartHeight) {
    if (!coordinate || chartWidth <= 0 || chartHeight <= 0) {
        return undefined;
    }

    const centerX = chartWidth / 2;
    const centerY = chartHeight / 2;

    let dx = coordinate.x - centerX;
    let dy = coordinate.y - centerY;
    const dist = Math.hypot(dx, dy) || 1;
    dx /= dist;
    dy /= dist;

    let x = coordinate.x + dx * TOOLTIP_RADIAL_OFFSET;
    let y = coordinate.y + dy * TOOLTIP_RADIAL_OFFSET;

    const maxX = chartWidth - TOOLTIP_ESTIMATE.width - TOOLTIP_MARGIN;
    const maxY = chartHeight - TOOLTIP_ESTIMATE.height - TOOLTIP_MARGIN;

    if (y > centerY || y > maxY) {
        y = coordinate.y - TOOLTIP_ESTIMATE.height - TOOLTIP_RADIAL_OFFSET;
    }

    if (x > maxX) {
        x = coordinate.x - TOOLTIP_ESTIMATE.width - TOOLTIP_RADIAL_OFFSET;
    }

    x = Math.min(Math.max(x, TOOLTIP_MARGIN), maxX);
    y = Math.min(Math.max(y, TOOLTIP_MARGIN), maxY);

    return { x, y };
}

function areTooltipPositionsEqual(previous, next) {
    if (previous === next) return true;
    if (!previous || !next) return !previous && !next;
    return previous.x === next.x && previous.y === next.y;
}

function OverviewTooltipContent({
    active,
    payload,
    coordinate,
    total,
    chartSize,
    onPositionChange,
}) {
    const tooltipPosition = useMemo(() => {
        if (!active || !coordinate || chartSize.width <= 0 || chartSize.height <= 0) {
            return undefined;
        }

        return getOverviewTooltipPosition(
            coordinate,
            chartSize.width,
            chartSize.height
        );
    }, [active, coordinate?.x, coordinate?.y, chartSize.width, chartSize.height]);

    useLayoutEffect(() => {
        onPositionChange(tooltipPosition);
    }, [tooltipPosition, onPositionChange]);

    if (!active || !payload?.length) return null;

    const item = payload[0]?.payload;
    const pct = total ? (item.value / total) * 100 : 0;

    return (
        <div className="remediation-dashboard-tooltip">
            <div className="remediation-dashboard-tooltip-title">
                {item.name}
            </div>
            <div>Total: {formatInt(item.value)}</div>
            <div>Open: {formatInt(item.open)}</div>
            <div>Closed: {formatInt(item.closed)}</div>
            <div>{formatPct(pct)} of all cases</div>
        </div>
    );
}

function OverviewDonutTooltip({ total, chartSize }) {
    const [tooltipPosition, setTooltipPosition] = useState(undefined);

    const handlePositionChange = useCallback((nextPosition) => {
        setTooltipPosition((previousPosition) =>
            areTooltipPositionsEqual(previousPosition, nextPosition)
                ? previousPosition
                : nextPosition
        );
    }, []);

    const renderTooltipContent = useCallback(
        (props) => (
            <OverviewTooltipContent
                {...props}
                total={total}
                chartSize={chartSize}
                onPositionChange={handlePositionChange}
            />
        ),
        [total, chartSize, handlePositionChange]
    );

    return (
        <Tooltip
            wrapperStyle={{ zIndex: 10, pointerEvents: "none" }}
            position={tooltipPosition}
            offset={0}
            reverseDirection={{ x: true, y: true }}
            isAnimationActive={false}
            content={renderTooltipContent}
        />
    );
}

export default function RemediationVisualOverview() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([]);
    const chartRef = useRef(null);
    const [chartSize, setChartSize] = useState({ width: 0, height: 260 });

    useEffect(() => {
        const chartElement = chartRef.current;
        if (!chartElement) return undefined;

        const updateChartSize = () => {
            setChartSize({
                width: chartElement.clientWidth,
                height: chartElement.clientHeight,
            });
        };

        updateChartSize();

        const observer = new ResizeObserver(updateChartSize);
        observer.observe(chartElement);

        return () => observer.disconnect();
    }, [loading, rows.length]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await fetchWithAuth(
                    `${API_BASE}/ainstein/in/dashboard?chart_key=cases_by_type`
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const payload = await response.json();
                const normalized = normalizeRows(payload);

                if (!cancelled) {
                    setRows(normalized);
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load remediation overview");
                    setRows([]);
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const total = useMemo(
        () => rows.reduce((sum, row) => sum + Number(row.value || 0), 0),
        [rows]
    );

    return (
        <section className="rd-visual-card rd-overview-card">
            <div className="rd-visual-card-header">
                <div className="rd-visual-title-row">
                    <span className="rd-visual-icon-dot" />
                    <h3 className="rd-visual-card-title">Remediation Overview</h3>
                </div>
            </div>

            {loading ? (
                <div className="remediation-dashboard-loading">
                    <span className="remediation-dashboard-loading-spinner" />
                    <span>Loading remediation overview...</span>
                </div>
            ) : error ? (
                <div className="remediation-dashboard-empty-state">{error}</div>
            ) : rows.length === 0 ? (
                <div className="remediation-dashboard-empty-state">
                    No remediation overview data available.
                </div>
            ) : (
                <div className="rd-overview-layout">
                    <div className="rd-overview-chart" ref={chartRef}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={rows}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius="58%"
                                    outerRadius="82%"
                                    paddingAngle={2}
                                    isAnimationActive={false}
                                >
                                    {rows.map((row, index) => (
                                        <Cell
                                            key={row.name}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>

                                <OverviewDonutTooltip total={total} chartSize={chartSize} />
                            </PieChart>
                        </ResponsiveContainer>

                        <div className="rd-overview-center">
                            <div className="rd-overview-center-value">{formatInt(total)}</div>
                            <div className="rd-overview-center-label">Cases</div>
                        </div>
                    </div>

                    <div className="rd-overview-legend">
                        <div className="rd-overview-legend-title">Remediation Types</div>

                        {rows.slice(0, 8).map((row, index) => {
                            const pct = total ? (row.value / total) * 100 : 0;

                            return (
                                <div key={row.name} className="rd-overview-legend-item">
                                    <span
                                        className="rd-overview-legend-color"
                                        style={{ background: COLORS[index % COLORS.length] }}
                                    />
                                    <span className="rd-overview-legend-name">{row.name}</span>
                                    <span className="rd-overview-legend-pct">
                                        {formatPct(pct)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
}